import React from "react";

import {
  LabGroupAvatar,
  LabGroupPill,
  statusKindFor,
} from "./MLabGroupTile";
import {
  ConstellationGroup,
  LabParameterGroupId,
} from "@/types/lab-insights";

import styles from "./MLabSnapshot.module.css";

const POSITION_KEYS = [
  "topLeft",
  "topRight",
  "midLeft",
  "midRight",
  "bottomLeft",
  "bottomRight",
] as const;

type PositionKey = (typeof POSITION_KEYS)[number];

// SVG viewBox matches the Figma frame's constellation area (391×430 — the
// vertical span between the top of the Blood Count pill and the bottom of
// the Kidney pill). preserveAspectRatio="none" lets the SVG stretch with
// the container so rays stay connected at smaller sizes.
const VIEWBOX_W = 391;
const VIEWBOX_H = 430;

// Endpoint coordinates for each connector ray inside the 391×430 viewBox.
// (cx, cy) is the START — pulled ~42px out of the central halo toward the
// avatar. (ix, iy) is the END — ~28px before the avatar center, so the
// ray ends just outside the icon ring with a clean ~3px gap.
//
// Avatar centers (Figma frame 2779:55311, origin shifted to y=71). Top
// row shifted 12px DOWN from Figma per the audit's "no touch flagged
// text" requirement; the other 4 are Figma-exact:
//   Blood Count    (107.5,  90)   Liver        (271.5, 105)
//   Lipid          ( 72,   171)   Thyroid      (313,   186)
//   Blood Sugar    (100,   331)   Kidney       (287,   352)
// Center halo anchored at 45% of container (≈ (194.5, 193.5)) — see
// .center / .sunburst rules in MLabSnapshot.module.css for why we shift
// down from Figma's literal 40.81%.
const RAY_ENDPOINTS: Record<
  PositionKey,
  { cx: number; cy: number; ix: number; iy: number }
> = {
  topLeft:     { cx: 168, cy: 161, ix: 126, iy: 111 }, // Blood Count
  topRight:    { cx: 222, cy: 162, ix: 253, iy: 126 }, // Liver Function
  midLeft:     { cx: 153, cy: 186, ix: 100, iy: 176 }, // Lipid Profile
  midRight:    { cx: 236, cy: 191, ix: 285, iy: 188 }, // Thyroid
  bottomLeft:  { cx: 171, cy: 228, ix: 116, iy: 308 }, // Blood Sugar
  bottomRight: { cx: 216, cy: 230, ix: 273, iy: 328 }, // Kidney Function
};

// Tapered ray dimensions — half-widths perpendicular to the ray axis.
// Near-pointed at the center end, wide at the avatar end, so the ray
// fans out toward each tile (matches Figma direction).
const RAY_HALF_WIDTH_START = 1.2;  // = 2.4px wide at center end (near-point)
const RAY_HALF_WIDTH_END = 4;      // = 8px wide at avatar end

// Maps each group id to the slot it should occupy in the constellation.
// Mirrors the Figma layout exactly. Falls back to array index for groups
// that aren't in the map (e.g. an unexpected default-6 ordering).
const GROUP_POSITION_MAP: Partial<
  Record<LabParameterGroupId, PositionKey>
> = {
  // Human
  [LabParameterGroupId.CBC]: "topLeft",
  [LabParameterGroupId.LFT]: "topRight",
  [LabParameterGroupId.LIPID]: "midLeft",
  [LabParameterGroupId.THYROID]: "midRight",
  [LabParameterGroupId.SUGAR]: "bottomLeft",
  [LabParameterGroupId.KFT]: "bottomRight",
  // Pet
  [LabParameterGroupId.CBC_PET]: "topLeft",
  [LabParameterGroupId.LFT_PET]: "topRight",
  [LabParameterGroupId.URINE_PET]: "midLeft",
  [LabParameterGroupId.THYROID_PET]: "midRight",
  [LabParameterGroupId.SUGAR_PET]: "bottomLeft",
  [LabParameterGroupId.KFT_PET]: "bottomRight",
};

// Figma: 26 rays evenly spaced (360 / 26 ≈ 13.85° apart) around the
// central "You" disc. Each ray = 6.805 × 11.909 with the same purple
// linear gradient (#DEC9EF → #BCA8EF), per Figma inspect on 2779:55317.
const SUNBURST_RAY_COUNT = 26;

interface MLabSnapshotProps {
  centerLabel: string;
  groups: ConstellationGroup[];
  onGroupClick?: (groupId: LabParameterGroupId) => void;
  className?: string;
}

const Sunburst: React.FC = () => {
  const rays = [];
  for (let i = 0; i < SUNBURST_RAY_COUNT; i++) {
    const angle = (360 / SUNBURST_RAY_COUNT) * i;
    rays.push(
      <span
        key={i}
        className={styles.sunburstRay}
        style={{
          transform: `translate(-50%, 0) rotate(${angle}deg) translateY(40px)`,
        }}
        aria-hidden
      />
    );
  }
  return <div className={styles.sunburst}>{rays}</div>;
};

const STATUS_GRADIENT_STOPS: Record<
  "flagged" | "allGood" | "noResults",
  { from: string; to: string }
> = {
  flagged: { from: "#EFCEC9", to: "#E85656" },
  allGood: { from: "#DEC9EF", to: "#BCA8EF" },
  noResults: { from: "#E4DEDE", to: "#B3A8A8" },
};

const Connectors: React.FC<{
  positioned: Array<{ group: ConstellationGroup; position: PositionKey }>;
}> = ({ positioned }) => (
  <svg
    className={styles.connectorsSvg}
    viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
    preserveAspectRatio="none"
    aria-hidden
  >
    <defs>
      {/* One gradient per ray, defined in user-space coords matching the
          ray's start/end. This is required because objectBoundingBox-based
          gradients collapse on zero-area boxes (a horizontal line has 0
          height) — that's why the mid-row left/right rays were rendering
          empty. Defining direction in viewBox coords avoids that. */}
      {positioned.map(({ group, position }) => {
        const status = statusKindFor(group);
        const stops = STATUS_GRADIENT_STOPS[status];
        const { cx, cy, ix, iy } = RAY_ENDPOINTS[position];
        return (
          <linearGradient
            key={`grad-${group.groupId}`}
            id={`labSnapshotRay-${group.groupId}`}
            gradientUnits="userSpaceOnUse"
            x1={cx}
            y1={cy}
            x2={ix}
            y2={iy}
          >
            <stop offset="0%" stopColor={stops.from} />
            <stop offset="100%" stopColor={stops.to} />
          </linearGradient>
        );
      })}
    </defs>
    {positioned.map(({ group, position }) => {
      const { cx, cy, ix, iy } = RAY_ENDPOINTS[position];
      // Build a tapered polygon (trapezoid) along the ray axis:
      // wide perpendicular half-width at the center end, near-pointed at
      // the avatar end. Replaces the constant-width <line> so rays match
      // the Figma design's tapered look instead of having blunt round caps.
      const dx = ix - cx;
      const dy = iy - cy;
      const len = Math.hypot(dx, dy) || 1;
      const px = -dy / len; // perpendicular unit (rotated +90°)
      const py = dx / len;
      const w1 = RAY_HALF_WIDTH_START;
      const w2 = RAY_HALF_WIDTH_END;
      const points = [
        [cx + w1 * px, cy + w1 * py],
        [ix + w2 * px, iy + w2 * py],
        [ix - w2 * px, iy - w2 * py],
        [cx - w1 * px, cy - w1 * py],
      ]
        .map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`)
        .join(" ");
      return (
        <polygon
          key={group.groupId}
          points={points}
          fill={`url(#labSnapshotRay-${group.groupId})`}
          opacity={0.85}
        />
      );
    })}
  </svg>
);

const positionGroups = (
  groups: ConstellationGroup[]
): Array<{ group: ConstellationGroup; position: PositionKey }> => {
  const taken = new Set<PositionKey>();
  const placed: Array<{ group: ConstellationGroup; position: PositionKey }> =
    [];
  const unplaced: ConstellationGroup[] = [];

  // First pass: place groups with an explicit slot in the map.
  for (const group of groups.slice(0, 6)) {
    const slot = GROUP_POSITION_MAP[group.groupId];
    if (slot && !taken.has(slot)) {
      taken.add(slot);
      placed.push({ group, position: slot });
    } else {
      unplaced.push(group);
    }
  }

  // Second pass: fill any remaining slots in canonical order with the
  // leftover groups (defensive — covers an unexpected default-6 order).
  for (const group of unplaced) {
    const slot = POSITION_KEYS.find((p) => !taken.has(p));
    if (!slot) break;
    taken.add(slot);
    placed.push({ group, position: slot });
  }

  return placed;
};

export const MLabSnapshot: React.FC<MLabSnapshotProps> = ({
  centerLabel,
  groups,
  onGroupClick,
  className,
}) => {
  const positioned = positionGroups(groups);

  // Per-element interactivity mirrors MLabGroupTile: "No results" groups
  // aren't clickable. Both the pill and the avatar fire the same handler
  // so users can tap either to drill into the group.
  const handleActivate = (group: ConstellationGroup) => {
    if (statusKindFor(group) === "noResults") return;
    onGroupClick?.(group.groupId);
  };
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    group: ConstellationGroup,
  ) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    if (statusKindFor(group) === "noResults") return;
    event.preventDefault();
    onGroupClick?.(group.groupId);
  };

  return (
    <div
      className={[styles.snapshot, className ?? ""].filter(Boolean).join(" ")}
    >
      <Connectors positioned={positioned} />
      <Sunburst />
      <div className={styles.center}>
        <span className={styles.centerName} title={centerLabel}>
          {centerLabel}
        </span>
      </div>

      {/* Avatars — absolutely positioned at exact Figma coords (see
          `.avatarSlot--*` in MLabSnapshot.module.css). Rendered before
          pills so they sit underneath in z-order when they touch (matches
          the Figma design's intentional minor overlap). */}
      {positioned.map(({ group, position }) => {
        const interactive = statusKindFor(group) !== "noResults";
        return (
          <div
            key={`avatar-${group.groupId}`}
            className={[
              styles.avatarSlot,
              styles[`avatarSlot--${position}`],
            ]
              .filter(Boolean)
              .join(" ")}
            role={interactive ? "button" : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-disabled={!interactive}
            aria-label={interactive ? group.groupName : undefined}
            onClick={interactive ? () => handleActivate(group) : undefined}
            onKeyDown={
              interactive ? (e) => handleKeyDown(e, group) : undefined
            }
            style={interactive ? undefined : { cursor: "default" }}
          >
            <LabGroupAvatar group={group} />
          </div>
        );
      })}

      {/* Pills — absolutely positioned at exact Figma coords (see
          `.pillSlot--*` in MLabSnapshot.module.css). */}
      {positioned.map(({ group, position }) => {
        const interactive = statusKindFor(group) !== "noResults";
        return (
          <div
            key={`pill-${group.groupId}`}
            className={[styles.pillSlot, styles[`pillSlot--${position}`]]
              .filter(Boolean)
              .join(" ")}
            role={interactive ? "button" : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-disabled={!interactive}
            onClick={interactive ? () => handleActivate(group) : undefined}
            onKeyDown={
              interactive ? (e) => handleKeyDown(e, group) : undefined
            }
            style={interactive ? undefined : { cursor: "default" }}
          >
            <LabGroupPill
              group={group}
              showChevron={interactive}
              className={styles.snapshotPill}
            />
          </div>
        );
      })}
    </div>
  );
};

export default MLabSnapshot;
